import { useState, useEffect } from "react";
import axios from "axios";
import { X, RefreshCw, Settings, PhoneMissed, UserCheck, Save, Check, Edit2 } from "lucide-react";
import getApiUrl from './utils/getApiUrl';

const API = `${getApiUrl()}/api`;

function AdminPage({ onBack, embedded = false }) {
  const [users, setUsers] = useState([]);
  const [nrpClients, setNrpClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nrpLoading, setNrpLoading] = useState(true);
  const [minCount, setMinCount] = useState(3);
  const [editingUser, setEditingUser] = useState(null);
  const [editCrmName, setEditCrmName] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, [minCount]);

  const loadData = async () => {
    setLoading(true);
    setNrpLoading(true);
    
    try {
      const usersRes = await axios.get(`${API}/ringover/users/mapping`);
      setUsers(usersRes.data.users || []);
    } catch (err) {
      console.error(err);
    }
    
    try {
      const nrpRes = await axios.get(`${API}/ringover/nrp/clients?min_count=${minCount}`);
      setNrpClients(nrpRes.data.clients || []);
    } catch (err) {
      console.error(err);
    }
    
    setLoading(false);
    setNrpLoading(false);
  };
  
  const startEditing = (user) => {
    setEditingUser(user.ringover_user_id);
    setEditCrmName(user.crm_name || "");
  };
  
  const saveMapping = async (userId) => {
    if (!editCrmName.trim()) return;
    
    setSaving(true);
    try {
      const user = users.find(u => u.ringover_user_id === userId);
      await axios.post(`${API}/ringover/users/mapping`, {
        ringover_user_id: userId,
        crm_name: editCrmName.trim(),
        email: user?.ringover_email || ""
      });
      setEditingUser(null);
      setEditCrmName("");
      await loadData();
    } catch (err) {
      console.error("Erreur sauvegarde mapping:", err);
      alert("Erreur lors de la sauvegarde");
    }
    setSaving(false);
  };
  
  const cancelEditing = () => {
    setEditingUser(null);
    setEditCrmName("");
  };

  return (
    <div className={embedded ? "h-full bg-slate-900 text-white" : "min-h-screen bg-slate-900 text-white"}>
      <header className="bg-slate-950 border-b border-slate-800 px-6 py-4">
        <div className="flex items-center gap-4">
          {!embedded && (
            <button onClick={onBack} className="p-2 hover:bg-slate-800 rounded-lg">
              <X className="w-5 h-5 text-slate-400" />
            </button>
          )}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-600 rounded-lg flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-lg">Administration Ringover</h1>
              <p className="text-xs text-slate-400">Gestion des utilisateurs et NRP</p>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6 space-y-6 max-w-6xl mx-auto">
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-4">
            <PhoneMissed className="w-5 h-5 text-red-400" />
            <h2 className="font-bold">Clients NRP ({nrpClients.length})</h2>
            <select
              value={minCount}
              onChange={(e) => setMinCount(parseInt(e.target.value))}
              className="bg-slate-700 border border-slate-600 rounded px-2 py-1 text-sm ml-auto"
            >
              <option value={2}>2+ tentatives</option>
              <option value={3}>3+ tentatives</option>
              <option value={5}>5+ tentatives</option>
            </select>
          </div>
          
          {nrpLoading ? (
            <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {nrpClients.map((c, i) => (
                <div key={i} className="bg-slate-900 p-3 rounded flex justify-between">
                  <span>{c.contact_name || "Inconnu"}</span>
                  <span className="text-red-400">{c.nrp_count}x NRP</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-slate-800 border border-slate-700 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-4">
            <UserCheck className="w-5 h-5 text-blue-400" />
            <h2 className="font-bold">Mapping Utilisateurs ({users.length})</h2>
          </div>
          
          {loading ? (
            <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {users.map((u) => (
                <div key={u.ringover_user_id} className="bg-slate-900 p-3 rounded flex justify-between items-center">
                  <div>
                    <p className="font-medium">{u.ringover_name || "ID: " + u.ringover_user_id}</p>
                    <p className="text-xs text-slate-400">{u.ringover_email}</p>
                  </div>
                  
                  {editingUser === u.ringover_user_id ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={editCrmName}
                        onChange={(e) => setEditCrmName(e.target.value)}
                        placeholder="Nom CRM"
                        className="bg-slate-800 border border-slate-600 rounded px-2 py-1 text-sm w-32"
                        autoFocus
                      />
                      <button
                        onClick={() => saveMapping(u.ringover_user_id)}
                        disabled={saving || !editCrmName.trim()}
                        className="p-1 bg-green-600 hover:bg-green-500 disabled:opacity-50 rounded"
                      >
                        {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={cancelEditing}
                        className="p-1 bg-slate-600 hover:bg-slate-500 rounded"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <span className={u.is_mapped ? "text-green-400" : "text-yellow-400"}>
                        {u.crm_name || "Non mappé"}
                      </span>
                      <button
                        onClick={() => startEditing(u)}
                        className="p-1 hover:bg-slate-700 rounded"
                        title="Modifier le mapping"
                      >
                        <Edit2 className="w-4 h-4 text-slate-400 hover:text-white" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminPage;
